import {
  FieldElementUnion,
  FormData,
  FormRepeatableUnitValue,
  FormSchema,
  IFormSchemaV2,
  InputElementUnion,
  isDateElement,
  isFileInputElement,
  isInputElement,
  RefkeyValueType,
} from "@graffer-inc/form-schema";

import {
  ApplyStatus,
  statusTextForConsole,
} from "../../application/application-entity";
import {
  isKintoneSubtableFormField,
  KintoneColumnFileValue,
  KintoneColumnLeafValue,
  KintoneColumnSubtableValue,
  KintoneFormField,
  KintoneRecord,
} from "../kintone";
import { KintoneFormFieldWithParentSubtableCode } from "../kintone-export-field-correspondence";
import {
  ElementFieldMapping,
  MetadataFieldMapping,
} from "../kintone-export-setting";
import { toJST } from "./to-jst";

export type MapFormDataToRecordsProps = Record<
  MapFormDataToRecordProps["submissionId"],
  MapFormDataToRecordProps
>;

export type MapFormDataToRecordProps = {
  submissionId: string;
  submittedAtUnixMilli: number;
  procedureName: string;
  applicationStatus: ApplyStatus; // アンケートを入力ソースとしてカバーする場合、うまく対応する
  formSchema: IFormSchemaV2;
  document: FormData;
  kintoneFields: Record<KintoneFormField["code"], KintoneFormField>;

  elementFieldMappings: ElementFieldMapping[];
  metadataFieldMapping: MetadataFieldMapping;

  refKeyFileKeyPairs: RefKeyFileKeyPairs;
};

export type RefKeyFileKeyPairs = Record<
  // index
  // graffer 側でフォームの添付ファイルを保存する際にキーとして使っている文字列
  RefkeyValueType,
  // value
  // kintone 側の一時保存領域へファイルを保存した際に返却される fileKey
  // https://cybozu.dev/ja/kintone/docs/rest-api/files/upload-file/#response
  string
>;

export type MapFormDataToRecordsResult = Record<
  MapFormDataToRecordProps["submissionId"],
  MapFormDataToRecordResult
>;

export type MapFormDataToRecordResult = {
  record: KintoneRecord;
};

export const mapFormDataToRecords = (
  arg: MapFormDataToRecordsProps,
): MapFormDataToRecordsResult => {
  const result: MapFormDataToRecordsResult = {};

  Object.entries(arg).forEach(([submissionId, props]) => {
    result[submissionId] = mapFormDataToRecord(props);
  });

  return result;
};

export const mapFormDataToRecord = (
  arg: MapFormDataToRecordProps,
): MapFormDataToRecordResult => {
  const { record } = mapFormDataToRecordByElementFieldMapping(arg);

  const {
    submissionId,
    submittedAtUnixMilli,
    procedureName,
    applicationStatus,
    metadataFieldMapping,
    kintoneFields,
  } = arg;

  record[metadataFieldMapping.submissionIdFieldCode] = {
    value: submissionId,
  };

  if (!!metadataFieldMapping.procedureNameFieldCode) {
    record[metadataFieldMapping.procedureNameFieldCode] = {
      value: procedureName,
    };
  }

  const {
    yearInJST,
    monthInJST,
    dateInJST,
    hoursInJST,
    minutesInJST,
    secondsInJST,
  } = toJST(new Date(submittedAtUnixMilli));
  if (
    kintoneFields[metadataFieldMapping.submittedAtFieldCode].type === "DATETIME"
  ) {
    // YYYY-MM-DDTHH:MM:SS±HH:MM フォーマットで登録する
    // https://cybozu.dev/ja/kintone/docs/rest-api/overview/kintone-rest-api-overview/#format-date-and-time
    record[metadataFieldMapping.submittedAtFieldCode] = {
      value: `${yearInJST}-${String(monthInJST).padStart(2, "0")}-${String(
        dateInJST,
      ).padStart(2, "0")}T${String(hoursInJST).padStart(2, "0")}:${String(
        minutesInJST,
      ).padStart(2, "0")}:${String(secondsInJST).padStart(2, "0")}+09:00`,
    };
  } else {
    // YYYY-MM-DD フォーマットで登録する
    // https://cybozu.dev/ja/kintone/docs/rest-api/overview/kintone-rest-api-overview/#format-date-and-time
    record[metadataFieldMapping.submittedAtFieldCode] = {
      value: `${yearInJST}-${String(monthInJST).padStart(2, "0")}-${String(
        dateInJST,
      ).padStart(2, "0")}`,
    };
  }

  if (!!metadataFieldMapping.submissionStatusFieldCode) {
    record[metadataFieldMapping.submissionStatusFieldCode] = {
      value: statusTextForConsole(applicationStatus),
    };
  }

  return {
    record,
  };
};

const mapFormDataToRecordByElementFieldMapping = ({
  formSchema,
  document,
  kintoneFields,
  elementFieldMappings,
  refKeyFileKeyPairs,
}: MapFormDataToRecordProps): MapFormDataToRecordResult => {
  const record: KintoneRecord = {};

  const elements = FormSchema.fromJSON(formSchema)
    .flattenDeep()
    .reduce<Record<FieldElementUnion["id"], FieldElementUnion>>(
      (prev, curr) => {
        prev[curr.id] = curr;
        return prev;
      },
      {},
    );
  const kintoneFieldsWithParentCode = Object.entries(kintoneFields).reduce<
    Record<KintoneFormField["code"], KintoneFormFieldWithParentSubtableCode>
  >((prev, [key, value]) => {
    prev[key] = value;
    if (isKintoneSubtableFormField(value)) {
      Object.values(value.fields).forEach((field) => {
        prev[field.code] = {
          ...field,
          parentSubtableCode: value.code,
        };
      });
    }
    return prev;
  }, {});

  elementFieldMappings.forEach(({ formElementId, kintoneFieldCode }) => {
    const element = elements[formElementId];

    // elementFieldMappings が validateElementFieldMappings を
    // パスしていれば input element にしかならない。
    if (!isInputElement(element)) {
      throw new Error(
        `element is not input element: ${element.id}, ${element.elementType}`,
      );
    }

    // repeatable の子要素ではない、通常の input element の場合
    if (!element.parent) {
      const value = document[element.id];

      // 任意項目の場合は値が存在しないことがある
      if (!value) {
        return;
      }

      record[kintoneFieldCode] = {
        value: formatLeafValue(
          value as KintoneColumnLeafValue,
          element,
          refKeyFileKeyPairs,
        ),
      };

      return;
    }

    // 以下、 repeatable の子要素だった場合

    const repeatableElementId = element.parent.repeatable.id;
    const subtableFieldId =
      kintoneFieldsWithParentCode[kintoneFieldCode].parentSubtableCode!;

    const repeatableValue = document[repeatableElementId] as
      | FormRepeatableUnitValue[]
      | undefined;

    // 任意項目の場合は値が存在しないことがある
    if (!repeatableValue) {
      return;
    }

    const subtableValue: { value: KintoneColumnSubtableValue } = !!record[
      subtableFieldId
    ]
      ? (record[subtableFieldId] as { value: KintoneColumnSubtableValue })
      : {
          value: Array.from({ length: repeatableValue.length }).map(() => {
            return { value: {} };
          }),
        };

    repeatableValue.forEach((unitValue, i) => {
      const value = unitValue[element.id];
      // 任意項目の場合は値が存在しないことがある
      if (!value) {
        return;
      }
      subtableValue.value[i].value = {
        ...subtableValue.value[i].value,
        [kintoneFieldCode]: {
          value: formatLeafValue(
            value as KintoneColumnLeafValue,
            element,
            refKeyFileKeyPairs,
          ),
        },
      };
    });

    record[subtableFieldId] = subtableValue;
  });

  return {
    record,
  };
};

const formatLeafValue = (
  value: KintoneColumnLeafValue,
  element: InputElementUnion,
  refKeyFileKeyPairs: RefKeyFileKeyPairs,
): KintoneColumnLeafValue => {
  if (isDateElement(element)) {
    return formatDate(value as string);
  }

  if (isFileInputElement(element)) {
    return formatFile(value as string, refKeyFileKeyPairs);
  }

  if ("enum" in element) {
    // value の型が string | string[] | KintoneColumnFileValue の都合上
    // string と array をどちらも if でくくっているが、
    // 実際のデータ上は KintoneColumnFileValue にはならないので、必ずどちらかに入る。

    if (typeof value === "string") {
      return element.enum.find((option) => option.value === value)!.text;
    }
    if (Array.isArray(value)) {
      return value.map(
        (v) => element.enum.find((option) => option.value === v)!.text,
      );
    }
  }

  return value;
};

// YYYY-MM-DD フォーマットへ統一する
// https://cybozu.dev/ja/kintone/docs/rest-api/overview/kintone-rest-api-overview/#format-date-and-time
// https://github.com/graffer-inc/graffer-schemas/blob/785cd742161e3aea435f4201ac40beffe4d26348/libs/packages/form-schema/lib/elements-schema/input-schema/date-input-element/date-input-element-format.ts
const formatDate = (value: string): string => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  if (/^\d{4}\/\d{2}\/\d{2}$/.test(value)) {
    return value.replace(/\//g, "-");
  }

  if (/^\d{8}$/.test(value)) {
    return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
  }

  // graffer schema 上の値ではエラーにならないはず
  throw new Error(`invalid date format: ${value}`);
};

const formatFile = (
  value: string,
  refKeyFileKeyPairs: RefKeyFileKeyPairs,
): KintoneColumnFileValue => {
  return [{ fileKey: refKeyFileKeyPairs[value] }];
};
